import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Navbar from "../Components/Navbar";
import Sidebar from "../Components/Sidebar";
import { useThemeContext } from "../context/ThemeContext";
import ImageChangemodel from "./ProfilePageModels/ImageChangemodel";
import ChangePassword from "./ChangePassword";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "../axios";
import { useParams } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import ArrowCircleRightRoundedIcon from "@mui/icons-material/ArrowCircleRightRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const url = process.env.REACT_APP_BE_URL;

const GeneralDetailsSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required!"),
  lastName: Yup.string().required("Last Name is required!"),
  email: Yup.string().email().required("Email is required!"),
});

const Profile = () => {
  const { userId } = useParams();
  const { isDarkMode } = useThemeContext();
  const items = JSON.parse(localStorage.getItem("userInfo"));

  const [data, setData] = useState(null);
  const [hideEmail, setHideEmail] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [checkUser, setCheckUser] = useState(false);
  const [openImageChnageModel, setopenImageChnageModel] = useState(false);
  const [openModelPassword, setOpenModelPassword] = useState(false);

  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    message: "",
  });

  const getData = async () => {
    const config = {
      headers: {
        Authorization: `Bearer ${items.token}`,
      },
    };
    const { data } = await axios.get(
      `${url}/user/${userId ? userId : items._id}`,
      config
    );
    setData(data);
  };

  useEffect(() => {
    getData();
    if (items._id === userId) setCheckUser(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (data) {
      setInitialValues({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
      });
    }
  }, [data]);

  const handleSubmit = async (values) => {
    setBtnDisabled(true);
    const config = {
      headers: {
        Authorization: `Bearer ${items.token}`,
      },
    };
    await axios.put(
      `${url}/user/${userId}`,
      {
        firstName: values.firstName,
        lastName: values.lastName,
      },
      config
    );
    setBtnDisabled(false);
    setAlert({
      showAlert: true,
      severity: "success",
      message: "Profile updated successfully!",
    });
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
          minHeight: "100vh",
          pt: "80px",
          display: "flex",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <Sidebar />

        <Box sx={{ flexGrow: 1, px: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 600, color: isDarkMode ? "#f1f5f9" : "#0f172a" }}>
                Profile
              </Typography>
              <Typography sx={{ fontSize: 14, color: isDarkMode ? "#cbd5e1" : "#64748b" }}>
                Manage your personal information
              </Typography>
            </Box>

            {/* Edit Profile button removed per UI update */}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                <CardContent>
                  {!data ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <ImageChangemodel
                        open={openImageChnageModel}
                        onClose={() => setopenImageChnageModel(false)}
                        userData={items}
                        updateData={getData}
                      />

                      <Box sx={{ display: "flex", gap: 3, alignItems: "center", mb: 3, flexWrap: "wrap" }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          badgeContent={
                            checkUser && (
                              <Tooltip title="Edit Profile Picture">
                                <IconButton
                                  sx={{
                                    bgcolor: "#3b82f6",
                                    border: "2px solid #fff",
                                    "&:hover": { bgcolor: "#2563eb" },
                                  }}
                                  onClick={() => setopenImageChnageModel(true)}
                                >
                                  <EditIcon sx={{ fontSize: "1rem", color: "#fff" }} />
                                </IconButton>
                              </Tooltip>
                            )
                          }
                        >
                          <Avatar
                            src={data?.profilePic || (data?.profilePic && data.profilePic.url) || ''}
                            sx={{ width: 120, height: 120, border: "4px solid #3b82f6", background: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)", boxShadow: "0 0 10px rgba(0,0,0,0.15)" }}
                          />
                        </Badge>

                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "1.25rem" }}>
                            {data.firstName} {data.lastName}
                          </Typography>
                          <Typography sx={{ color: "#6b7280", fontSize: "0.9rem" }}>
                            {data.email}
                          </Typography>
                        </Box>
                      </Box>

                      <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={GeneralDetailsSchema}
                        enableReinitialize
                      >
                        {({ errors, touched }) => (
                          <Form>
                            <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", mb: 2, color: "#111827" }}>
                              General Details
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Field as={TextField} fullWidth size="small" name="firstName" label="First Name" disabled={!checkUser} error={touched.firstName && !!errors.firstName} helperText={touched.firstName && errors.firstName} />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Field as={TextField} fullWidth size="small" name="lastName" label="Last Name" disabled={!checkUser} error={touched.lastName && !!errors.lastName} helperText={touched.lastName && errors.lastName} />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Field as={TextField} fullWidth size="small" name="email" label="Email" disabled type={hideEmail ? "text" : "password"} InputProps={{ endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton onClick={() => setHideEmail(!hideEmail)}>
                                          {hideEmail ? <VisibilityOff sx={{ color: "#ef4444" }} /> : <Visibility sx={{ color: "#10b981" }} />}
                                        </IconButton>
                                      </InputAdornment>
                                    ), }} />
                              </Grid>
                            </Grid>

                            {checkUser && (
                              <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                                <Button variant="contained" type="submit" disabled={btnDisabled} sx={{ textTransform: "none", background: "linear-gradient(to right, #3b82f6, #2563eb)", "&:hover": { background: "linear-gradient(to right, #2563eb, #1d4ed8)" } }}>
                                  Update Details
                                </Button>

                                <Button variant="outlined" sx={{ textTransform: "none", borderColor: "#3b82f6", color: "#3b82f6", "&:hover": { background: "#e0f2fe", borderColor: "#2563eb" } }} onClick={() => setOpenModelPassword(true)} endIcon={<ArrowCircleRightRoundedIcon sx={{ color: "#2563eb" }} />}>
                                  Reset Password
                                </Button>
                              </Box>
                            )}
                          </Form>
                        )}
                      </Formik>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <ChangePassword open={openModelPassword} onClose={() => setOpenModelPassword(false)} />

      <Snackbar open={alert.showAlert} autoHideDuration={6000} onClose={() => setAlert({ ...alert, showAlert: false })} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>
    </>
  );
};

export default Profile;
